import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { configurationsAPI } from "../../services/api";

const ConfigurationManager = ({
  onConfigurationChange,
  selectedConfiguration,
}) => {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });

  useEffect(() => {
    loadConfigurations();
  }, []);

  useEffect(() => {
    if (configurations.length > 0) {
      // First, try to restore from localStorage
      const savedConfigId = localStorage.getItem("selectedConfiguration");
      const savedConfigIdNum = savedConfigId
        ? parseInt(savedConfigId, 10)
        : null;

      // Check if saved configuration still exists in the list
      const savedConfigExists =
        savedConfigIdNum &&
        configurations.some((c) => c.id === savedConfigIdNum);

      if (savedConfigExists && !selectedConfiguration) {
        // Restore the saved configuration
        console.log(
          "ConfigurationManager: Restoring saved configuration:",
          savedConfigIdNum,
        );
        onConfigurationChange(savedConfigIdNum);
      } else if (!selectedConfiguration) {
        // No saved config or it doesn't exist, find active or use first one
        const activeConfig = configurations.find((c) => c.is_active);
        const configToSelect = activeConfig || configurations[0];
        // Ensure we pass a number
        const configId =
          typeof configToSelect.id === "string"
            ? parseInt(configToSelect.id, 10)
            : configToSelect.id;
        console.log(
          "ConfigurationManager: Auto-selecting configuration:",
          configId,
        );
        onConfigurationChange(configId);
        // Save the default selection
        localStorage.setItem("selectedConfiguration", configId.toString());
      }
    }
  }, [configurations]);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const response = await configurationsAPI.getAll();
      setConfigurations(response.data);
    } catch (err) {
      setError("Failed to load configurations");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingConfig(null);
    setFormData({ name: "", description: "" });
    setDialogOpen(true);
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({ name: config.name, description: config.description || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setError("");
      if (!formData.name || formData.name.trim() === "") {
        setError("Configuration name is required");
        return;
      }

      if (editingConfig) {
        await configurationsAPI.update(editingConfig.id, formData);
        setSuccess("Configuration updated successfully");
      } else {
        await configurationsAPI.create(formData);
        setSuccess("Configuration created successfully");
      }
      setDialogOpen(false);
      loadConfigurations();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save configuration");
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this configuration? All associated settings will be unlinked.",
      )
    ) {
      return;
    }
    try {
      await configurationsAPI.delete(id);
      setSuccess("Configuration deleted successfully");
      if (selectedConfiguration === id) {
        onConfigurationChange(null);
      }
      loadConfigurations();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete configuration");
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Configurations</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Create Configuration
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create and manage named configurations. Each configuration can have its
        own repository connections, DevOps connections, and AI settings.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {configurations.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No configurations found. Create your first configuration to get
          started.
        </Alert>
      ) : (
        <>
          <Box mb={2}>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Select Configuration</InputLabel>
              <Select
                value={selectedConfiguration || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Ensure we pass the value (could be string from Select, convert to number)
                  const configId =
                    typeof value === "string" ? parseInt(value, 10) : value;
                  console.log(
                    "ConfigurationManager: User selected configuration:",
                    configId,
                  );
                  // Save to localStorage for persistence
                  localStorage.setItem("selectedConfiguration", configId.toString());
                  onConfigurationChange(configId);
                }}
                label="Select Configuration"
              >
                {configurations.map((config) => (
                  <MenuItem key={config.id} value={config.id}>
                    {config.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Repositories</TableCell>
                  <TableCell>DevOps</TableCell>
                  <TableCell>AI Configs</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configurations.map((config) => (
                  <TableRow
                    key={config.id}
                    selected={selectedConfiguration === config.id}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          fontWeight={
                            selectedConfiguration === config.id
                              ? "bold"
                              : "normal"
                          }
                        >
                          {config.name}
                        </Typography>
                        {selectedConfiguration === config.id && (
                          <Chip label="Active" size="small" color="primary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{config.description || "-"}</TableCell>
                    <TableCell>{config.repo_count || 0}</TableCell>
                    <TableCell>{config.devops_count || 0}</TableCell>
                    <TableCell>{config.ai_count || 0}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(config)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(config.id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingConfig ? "Edit Configuration" : "Create Configuration"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Configuration Name"
            margin="normal"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            helperText="This name will be used to identify this configuration"
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingConfig ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ConfigurationManager;
